import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

const formatJson = (value) => {
  if (value == null || value === "") {
    return "{}";
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const parseJsonObject = (raw, label) => {
  const trimmed = String(raw || "").trim();
  if (!trimmed) {
    return {};
  }
  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (error) {
    throw new Error(`${label}: JSON non valido (${error.message})`);
  }
  if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error(`${label}: atteso un oggetto JSON`);
  }
  return parsed;
};

const EditableMetadataColumn = ({
  title,
  value,
  onChange,
  editable,
}) => (
  <div className="cm-raw-metadata-column">
    <Typography className="cm-raw-metadata-title" component="h3" variant="subtitle2">
      {title}
    </Typography>
    <div className="cm-raw-metadata-scroll-area">
      {editable ? (
        <TextField
          className="cm-raw-metadata-editor"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          multiline
          rows={1}
          fullWidth
          spellCheck={false}
          inputProps={{ className: "cm-raw-metadata-editor-input" }}
        />
      ) : (
        <pre className="cm-raw-metadata-json">{value}</pre>
      )}
    </div>
  </div>
);

EditableMetadataColumn.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  editable: PropTypes.bool,
};

const RawMetadataTab = ({
  feedConfig,
  mappingConfig,
  lastRunSummary,
  onSaveMetadata,
  saving = false,
}) => {
  const [feedConfigText, setFeedConfigText] = useState(() => formatJson(feedConfig));
  const [mappingConfigText, setMappingConfigText] = useState(() => formatJson(mappingConfig));
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");

  useEffect(() => {
    setFeedConfigText(formatJson(feedConfig));
  }, [feedConfig]);

  useEffect(() => {
    setMappingConfigText(formatJson(mappingConfig));
  }, [mappingConfig]);

  const lastRunSummaryText = useMemo(
    () => formatJson(lastRunSummary),
    [lastRunSummary],
  );

  const handleSave = useCallback(async () => {
    if (!onSaveMetadata) {
      return;
    }
    setSaveError("");
    setSaveSuccess("");
    try {
      const parsedFeedConfig = parseJsonObject(feedConfigText, "feed_config");
      const parsedMappingConfig = parseJsonObject(mappingConfigText, "mapping_config");
      await onSaveMetadata({
        feed_config: parsedFeedConfig,
        mapping_config: parsedMappingConfig,
      });
      setSaveSuccess("Configurazione salvata.");
    } catch (error) {
      setSaveError(error.message || "Errore nel salvataggio della configurazione");
    }
  }, [feedConfigText, mappingConfigText, onSaveMetadata]);

  return (
    <Paper className="cm-panel cm-raw-metadata-panel">
      <Box className="cm-raw-metadata-toolbar">
        <Typography component="p" variant="body2" className="cm-raw-metadata-hint">
          Visualizza e modifica (escluso last_run_summary) raw metadata.
        </Typography>
        <Button
          sx={{ maxHeight: "32px" }}
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={saving || !onSaveMetadata}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? "Salvataggio..." : "Salva configurazione"}
        </Button>
      </Box>
      {saveError ? <Alert severity="error" className="cm-raw-metadata-alert">{saveError}</Alert> : null}
      {saveSuccess ? <Alert severity="success" className="cm-raw-metadata-alert">{saveSuccess}</Alert> : null}
      <div className="cm-raw-metadata-grid">
        <EditableMetadataColumn
          title="feed_config"
          value={feedConfigText}
          onChange={setFeedConfigText}
          editable
        />
        <EditableMetadataColumn
          title="mapping_config"
          value={mappingConfigText}
          onChange={setMappingConfigText}
          editable
        />
        <EditableMetadataColumn
          title="last_run_summary"
          value={lastRunSummaryText}
          editable={false}
        />
      </div>
    </Paper>
  );
};

RawMetadataTab.propTypes = {
  feedConfig: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  mappingConfig: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  lastRunSummary: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onSaveMetadata: PropTypes.func,
  saving: PropTypes.bool,
};

export default RawMetadataTab;
